import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Alert, Anchor, Button, PasswordInput, PinInput, Stack, Text, TextInput, Title } from '@mantine/core';
import { useForm, schemaResolver } from '@mantine/form';
import { z } from 'zod';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import * as authApi from '@/features/auth/api';
import { passwordSchema } from '@/features/auth/password-utils';
import { userFacingMessage } from '@/shared/api/errorMessage';

/**
 * Recupero password con **codice a 6 cifre** (BE: `account/password-reset-code`).
 *
 * Tre passi in una sola pagina, di proposito: il codice vive 10 minuti e non arriva dentro un link,
 * quindi l'utente non deve mai cambiare scheda — niente stato da tramandare tra rotte, niente codice
 * nella URL.
 *
 * Convive con `/recovery-password` + `/update-password`, che restano la pagina del **vecchio** flusso
 * a link (token GUID nella mail): entrambi i flussi sono attivi lato BE e nessuno dei due tocca l'altro.
 */

const emailSchema = z.object({ email: z.string().email('Email non valida') });
const pwdSchema = z
  .object({ password: passwordSchema, confirm: z.string() })
  .refine((d) => d.password === d.confirm, { path: ['confirm'], message: 'Le password non coincidono' });

export const Route = createFileRoute('/_auth/password-reset')({ component: PasswordResetPage });

type Step = 'email' | 'code' | 'password';

function PasswordResetPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [ticket, setTicket] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /**
   * Si è arrivati al passo 2 **con un codice già in mano**, senza chiederne uno.
   *
   * ⚠️ Serve perché il passo 1 non è solo una schermata: `Invia codice` **genera** il codice e
   * spegne i precedenti. Chi ne ha già ricevuto uno — tipicamente mandato da un amministratore
   * dalla scheda utente — non aveva modo di arrivare al campo dove digitarlo: l'unica strada
   * passava da lì, e percorrendola bruciava proprio il codice che stava tenendo. Cambia solo la
   * frase in testa: il passo 2 è identico nei due casi.
   */
  const [hasExistingCode, setHasExistingCode] = useState(false);

  const emailForm = useForm({ mode: 'controlled', initialValues: { email: '' }, validate: schemaResolver(emailSchema, { sync: true }) });
  const pwdForm = useForm({
    mode: 'controlled',
    initialValues: { password: '', confirm: '' },
    validate: schemaResolver(pwdSchema, { sync: true }),
  });

  const errMessage = (err: unknown): string =>
    userFacingMessage(err, 'Errore');

  // ─── Passo 1: richiesta del codice ───────────────────────────────────────────
  const sendCode = async (target: string): Promise<boolean> => {
    setBusy(true);
    try {
      await authApi.requestPasswordResetCode(target);
      return true;
    } catch (err) {
      notifications.show({ color: 'red', title: 'Invio non riuscito', message: errMessage(err) });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const onSubmitEmail = emailForm.onSubmit(async (values) => {
    if (!(await sendCode(values.email))) return;
    setEmail(values.email);
    setCode('');
    setCodeError(null);
    setHasExistingCode(false);
    setStep('code');
  });

  /**
   * La porta laterale: si passa al passo 2 **senza generare niente**.
   *
   * L'email serve lo stesso — è la chiave con cui il server verifica il codice — quindi si
   * valida il campo che è già lì, invece di chiederla di nuovo al passo dopo.
   */
  const onUseExistingCode = () => {
    if (emailForm.validate().hasErrors) return;
    setEmail(emailForm.values.email);
    setCode('');
    setCodeError(null);
    setHasExistingCode(true);
    setStep('code');
  };

  const onResend = async () => {
    if (!(await sendCode(email))) return;
    setCode('');
    setCodeError(null);
    // Da qui in poi un codice gliel'abbiamo mandato davvero, e quello vecchio è spento.
    setHasExistingCode(false);
    notifications.show({ color: 'green', message: 'Ti abbiamo inviato un nuovo codice.' });
  };

  // ─── Passo 2: verifica del codice ────────────────────────────────────────────
  const onVerify = async () => {
    if (code.length < 6) {
      setCodeError('Inserisci il codice di 6 cifre');
      return;
    }
    setBusy(true);
    setCodeError(null);
    try {
      const res = await authApi.verifyPasswordResetCode(email, code);
      setTicket(res.ticket);
      setStep('password');
    } catch (err) {
      // Il BE distingue codice errato / scaduto / troppi tentativi e manda già il testo italiano.
      setCodeError(errMessage(err));
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  // ─── Passo 3: nuova password ─────────────────────────────────────────────────
  const onSubmitPassword = pwdForm.onSubmit(async ({ password }) => {
    setBusy(true);
    try {
      await authApi.confirmPasswordResetCode(email, ticket, password);
      notifications.show({ color: 'green', message: 'Password aggiornata: ora puoi accedere.' });
      void navigate({ to: '/login' });
    } catch (err) {
      notifications.show({ color: 'red', title: 'Cambio password non riuscito', message: errMessage(err) });
    } finally {
      setBusy(false);
    }
  });

  return (
    <Stack>
      <div>
        <Title order={2}>Recupera password</Title>
        <Text c="dimmed" size="sm">
          {step === 'email' && 'Ti invieremo un codice di 6 cifre via email.'}
          {/* Chi è arrivato dalla porta laterale un codice non l'ha appena ricevuto: dirgli
              «Abbiamo inviato» lo manderebbe ad aspettare una mail che non parte. */}
          {step === 'code' && (hasExistingCode
            ? `Inserisci il codice di sei cifre che hai ricevuto per ${email}.`
            : `Abbiamo inviato un codice a ${email}. Se l'indirizzo è registrato, arriva entro pochi minuti.`)}
          {step === 'password' && 'Codice verificato. Scegli la nuova password.'}
        </Text>
      </div>

      {step === 'email' && (
        <form onSubmit={onSubmitEmail}>
          <Stack>
            <TextInput label="Email" required type="email" autoComplete="email" {...emailForm.getInputProps('email')} />
            <Button type="submit" loading={busy} fullWidth size="md">Invia codice</Button>
            {/* La porta per chi il codice ce l'ha già — di solito mandato da un amministratore
                dalla scheda utente. Senza, l'unico modo di arrivare al campo del codice era
                chiederne un altro, che spegne quello appena ricevuto. */}
            <Text size="sm" ta="center">
              <Anchor component="button" type="button" onClick={onUseExistingCode}>
                Ho già un codice
              </Anchor>
            </Text>
            <Text size="sm" ta="center">
              <Anchor component={Link} to="/login">Torna al login</Anchor>
            </Text>
          </Stack>
        </form>
      )}

      {step === 'code' && (
        <Stack>
          <Stack align="center" gap="xs">
            <Text size="sm">Codice ricevuto via email</Text>
            <PinInput length={6} type="number" oneTimeCode value={code} onChange={setCode} error={!!codeError} />
          </Stack>
          {codeError && <Alert color="red" variant="light">{codeError}</Alert>}
          <Button onClick={onVerify} loading={busy} disabled={code.length < 6} fullWidth size="md">
            Verifica codice
          </Button>
          {/* ⚠️ Chiedere un codice nuovo **spegne quello in mano**: a chi è arrivato con un
              codice ricevuto non si offre «non l'hai ricevuto?», che lo inviterebbe a bruciarlo
              per sbaglio. Resta comunque raggiungibile, perché un codice scaduto è un caso vero. */}
          <Text size="sm" ta="center">
            {hasExistingCode ? 'Il codice non funziona più?' : 'Non hai ricevuto il codice?'}{' '}
            <Anchor component="button" type="button" onClick={onResend} disabled={busy}>
              {hasExistingCode ? 'Chiedine uno nuovo' : 'Invialo di nuovo'}
            </Anchor>
          </Text>
          <Text size="sm" ta="center">
            <Anchor component="button" type="button" onClick={() => { setHasExistingCode(false); setStep('email'); }}>Cambia email</Anchor>
          </Text>
        </Stack>
      )}

      {step === 'password' && (
        <form onSubmit={onSubmitPassword}>
          <Stack>
            <PasswordInput label="Nuova password" required autoComplete="new-password" {...pwdForm.getInputProps('password')} />
            <PasswordInput label="Conferma password" required autoComplete="new-password" {...pwdForm.getInputProps('confirm')} />
            <Button type="submit" loading={busy} fullWidth size="md">Aggiorna password</Button>
          </Stack>
        </form>
      )}
    </Stack>
  );
}
